import React, { useState, useEffect } from "react";
import {
  FiFilter,
  FiX,
  FiCheck,
  FiClock,
  FiUser,
  FiPhone,
  FiMail,
  FiDollarSign,
  FiCalendar,
  FiImage,
  FiRefreshCw,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
  FiXCircle,
  FiUserCheck,
  FiAlertCircle,
} from "react-icons/fi";
import { getRejectedDeposits } from "../../api/endpoints";
import Swal from "../../utils/swalCompat";

import PageHeader from "../../components/PageHeader";

const DepositRejected = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [baseUrl, setBaseUrl] = useState(""); // ✅ Add baseUrl state

  // ✅ Fetch Rejected Deposits
  const fetchRejectedDeposits = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        search: search.trim(),
        startDate,
        endDate,
      };

      const res = await getRejectedDeposits(params);
      console.log("Rejected Deposits:", res);

      // ✅ Save baseUrl from response
      if (res.data?.baseUrl) {
        setBaseUrl(res.data.baseUrl);
        console.log("Base URL set:", res.data.baseUrl);
      }

      setTransactions(res.data.transactions || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (error) {
      console.error("Error fetching rejected deposits:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to fetch rejected deposits",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRejectedDeposits();
  }, [page, search, startDate, endDate]);

  // ✅ Get Full Image URL (same as DepositPending)
  const getFullImageUrl = (screenshot) => {
    if (!screenshot) return "";
    return screenshot.startsWith("http")
      ? screenshot
      : `${baseUrl}/${screenshot}`;
  };

  // ✅ Format Date
  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ✅ Get User Name
  const getUserName = (item) => {
    if (item.userName && item.userName.trim() !== "" && item.userName !== "N/A") {
      return item.userName;
    }
    return "N/A";
  };

const getStatusBadge = (status) => {
  const statusMap = {
    REJECTED: { bg: "#fee2e2", color: "#dc2626", icon: <FiX />,     label: "REJECTED" },
    APPROVED: { bg: "#dcfce7", color: "#16a34a", icon: <FiCheck />, label: "APPROVED" },
    PENDING:  { bg: "#fef9c3", color: "#ca8a04", icon: <FiClock />, label: "PENDING" },
  };
  const info = statusMap[status] || statusMap.REJECTED;
  return (
    <span
      className="badge d-inline-flex align-items-center gap-1 px-3 py-2 fs-6"
      style={{ background: info.bg, color: info.color, fontWeight: 600 }}
    >
      {info.icon} {info.label}
    </span>
  );
};

  // ✅ Get Remark with Tooltip
  const getRemark = (remark) => {
    if (!remark || remark.trim() === "") {
      return <span className="text-muted">No remark</span>;
    }
    if (remark.length > 30) {
      return (
        <span title={remark}>
          {remark.substring(0, 30)}...
        </span>
      );
    }
    return <span>{remark}</span>;
  };

  return (
    <div className="lead-page">
      <div className="deposit-rejected-page">
        {/* Header */}
        <PageHeader
          eyebrow="Manual Payment"
          title="Deposit Rejected"
          subtitle="View all rejected deposit requests and remarks."
          icon={<FiXCircle />}
          tone="danger"
          stat={{ label: "Total Rejected", value: total }}
        />

        {/* Card */}
        <div className="card shadow-sm border-0">
          {/* Card Header */}
          <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap border-bottom">
            <h5 className="mb-0 fw-bold">
              <FiXCircle className="me-2 text-danger" />
              Rejected Deposit Requests
            </h5>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              {showFilter && (
                <>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search by User, UTR..."
                    value={search}
                    onChange={(e) => {
                      setPage(1);
                      setSearch(e.target.value);
                    }}
                    style={{ width: "200px" }}
                  />
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={startDate}
                    onChange={(e) => {
                      setPage(1);
                      setStartDate(e.target.value);
                    }}
                    style={{ width: "140px" }}
                  />
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={endDate}
                    onChange={(e) => {
                      setPage(1);
                      setEndDate(e.target.value);
                    }}
                    style={{ width: "140px" }}
                  />
                </>
              )}
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowFilter(!showFilter)}
              >
                <FiFilter /> Filter
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={fetchRejectedDeposits}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <>
                    <FiRefreshCw /> Refresh
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Card Body */}
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "50px" }}>#</th>
                    <th style={{ minWidth: "200px" }}>User Details</th>
                    <th style={{ width: "120px" }}>Amount</th>
                    <th style={{ minWidth: "130px" }}>UTR Number</th>
                    <th style={{ width: "120px" }}>Payment</th>
                    <th style={{ width: "80px" }}>Screenshot</th>
                    <th style={{ minWidth: "150px" }}>Rejected Date</th>
                    <th style={{ minWidth: "150px" }}>Remark</th>
                    <th style={{ width: "110px" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-2 text-muted">Loading rejected deposits...</div>
                      </td>
                    </tr>
                  ) : transactions.length > 0 ? (
                    transactions.map((item, index) => {
                      const imageUrl = getFullImageUrl(item.screenshot);
                      console.log("Image URL:", imageUrl); // Debugging
                      return (
                        <tr key={item._id}>
                          <td className="fw-bold">{(page - 1) * limit + index + 1}</td>

                          {/* User Details */}
                          <td>
                            <div className="user-info-box">
                              <div className="user-name">
                                <div className="user-icon">
                                  <FiUser size={14} />
                                </div>
                                <strong>{getUserName(item)}</strong>
                              </div>
                              <div className="user-detail">
                                <FiPhone />
                                <span>{item.userMobile || "N/A"}</span>
                              </div>
                              {/* <div className="user-detail">
                                <FiMail />
                                <span>{item.userEmail || "N/A"}</span>
                              </div>
                              <div className="user-referral">
                                Referral: {item.userReferral || "-"}
                              </div> */}
                            </div>
                          </td>

                          {/* Amount */}
                          <td>
                            <span className="fw-bold text-danger fs-5">
                              ₹ {item.amount?.toLocaleString() || 0}
                            </span>
                          </td>

                          {/* UTR Number */}
                          <td>
                            <span className="badge bg-secondary text-dark px-3 py-2">
                              {item.utrNumber || "N/A"}
                            </span>
                          </td>

                          {/* Payment Method */}
                          <td>
                            <span className="badge bg-secondary bg-opacity-10 text-dark">
                              {item.paymentMethod || "N/A"}
                            </span>
                          </td>

                          {/* Screenshot - ✅ Updated */}
                          <td>
                            {imageUrl ? (
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                  console.log("Opening image:", imageUrl);
                                  setSelectedImage(imageUrl);
                                }}
                              >
                                <FiEye /> View
                              </button>
                            ) : (
                              <span className="text-muted">N/A</span>
                            )}
                          </td>

                          {/* Rejected Date */}
                          <td>
                            <div className="small">
                              <FiCalendar size={10} className="me-1" />
                              {item.updatedAt ? formatDate(item.updatedAt) : "N/A"}
                            </div>
                          </td>

                          {/* Remark */}
                          <td>
                            <div className="small">
                              <FiAlertCircle size={10} className="me-1 text-warning" />
                              {getRemark(item.remark)}
                            </div>
                          </td>

                          {/* Status */}
                          <td>{getStatusBadge(item.status)}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <div className="text-muted">
                          <FiXCircle size={40} className="mb-3 d-block mx-auto text-secondary" />
                          <h5 className="fw-bold">No Rejected Deposits</h5>
                          <p className="small">No deposits have been rejected yet</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div>
                  <span className="text-muted small">
                    Showing {(page - 1) * limit + 1} to{" "}
                    {Math.min(page * limit, total)} of {total} entries
                  </span>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    <FiChevronLeft /> Previous
                  </button>
                  <span className="fw-bold px-3">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next <FiChevronRight />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ✅ Screenshot Modal - Updated */}
        {selectedImage && (
          <div
            className="modal show d-block"
            style={{
              backgroundColor: "rgba(0,0,0,0.7)",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            onClick={() => setSelectedImage(null)}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              style={{ maxWidth: "600px", width: "100%", margin: "auto" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Deposit Screenshot</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSelectedImage(null)}
                  />
                </div>
                <div className="modal-body text-center">
                  <img
                    src={selectedImage}
                    alt="Deposit Screenshot"
                    crossOrigin="anonymous"
                    className="img-fluid rounded"
                    style={{ maxHeight: "400px", objectFit: "contain" }}
                    onLoad={() => console.log("Image Loaded Successfully")}
                    onError={(e) => {
                      console.error("Image failed to load:", e.target.src);
                      e.target.style.display = 'none';
                      Swal.fire({
                        icon: "error",
                        title: "Error",
                        text: "Failed to load image. Please check if the URL is correct.",
                      });
                    }}
                  />
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setSelectedImage(null)}
                  >
                    Close
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => window.open(selectedImage, "_blank")}
                  >
                    Open Full Size
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DepositRejected;
